import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateAdminCredentials } from "../../Services/admin.service.ts";
import styles from "./AccountSettings.module.css";

const USERNAME_RULE = /^[a-z0-9._-]{3,32}$/;
const MIN_PASSWORD = 8;

const AccountSettings = () => {
  const dispatch = useDispatch();
  const currentUsername: string = useSelector((state: any) => state.auth.username);
  const [form, setForm] = useState({
    username: currentUsername,
    newPassword: "",
    confirmPassword: "",
    currentPassword: "",
  });
  const [status, setStatus] = useState<{ type: "error" | "success"; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const update = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setStatus(null);
  };

  const username = form.username.trim().toLowerCase();
  const usernameChanged = username !== currentUsername;
  const passwordChanged = form.newPassword.length > 0;

  const validate = () => {
    if (!USERNAME_RULE.test(username)) {
      return "Username needs 3–32 characters: lowercase letters, numbers, dots, dashes or underscores.";
    }
    if (passwordChanged && form.newPassword.length < MIN_PASSWORD) {
      return `New password needs at least ${MIN_PASSWORD} characters.`;
    }
    if (passwordChanged && form.newPassword !== form.confirmPassword) {
      return "The new passwords must match.";
    }
    if (!usernameChanged && !passwordChanged) {
      return "Change the username or enter a new password to save.";
    }
    if (!form.currentPassword) {
      return "Enter your current password to confirm the change.";
    }
    return "";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const problem = validate();
    if (problem) {
      setStatus({ type: "error", text: problem });
      return;
    }
    setSaving(true);
    const result = await updateAdminCredentials({
      currentPassword: form.currentPassword,
      username,
      ...(passwordChanged ? { newPassword: form.newPassword } : {}),
    });
    setSaving(false);
    if (!result.success) {
      setStatus({ type: "error", text: result.error });
      return;
    }
    dispatch({ type: "LOGIN", payload: { token: result.token, username: result.username } });
    setForm({ username: result.username, newPassword: "", confirmPassword: "", currentPassword: "" });
    setStatus({
      type: "success",
      text: "Account updated. Other signed-in sessions now need to log in again.",
    });
  };

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <h2>Account</h2>
        <p>Update the username and password used to sign in to this dashboard.</p>
      </header>

      <form className={styles.card} onSubmit={handleSubmit} noValidate>
        <section className={styles.section}>
          <h3>Sign-in details</h3>
          <label className={styles.field}>
            <span>Username</span>
            <input
              name="username"
              value={form.username}
              onChange={update}
              autoComplete="username"
              spellCheck={false}
            />
            <small>Lowercase letters, numbers, dots, dashes and underscores.</small>
          </label>
        </section>

        <section className={styles.section}>
          <h3>New password</h3>
          <div className={styles.row}>
            <label className={styles.field}>
              <span>New password</span>
              <input
                type="password"
                name="newPassword"
                value={form.newPassword}
                onChange={update}
                autoComplete="new-password"
                placeholder="Keep current password"
              />
              <small>At least {MIN_PASSWORD} characters.</small>
            </label>
            <label className={styles.field}>
              <span>Confirm new password</span>
              <input
                type="password"
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={update}
                autoComplete="new-password"
                disabled={!passwordChanged}
              />
            </label>
          </div>
        </section>

        <section className={styles.section}>
          <h3>Confirm it's you</h3>
          <label className={styles.field}>
            <span>Current password</span>
            <input
              type="password"
              name="currentPassword"
              value={form.currentPassword}
              onChange={update}
              autoComplete="current-password"
            />
          </label>
        </section>

        {status && (
          <div className={status.type === "error" ? styles.error : styles.success} role="status">
            {status.text}
          </div>
        )}

        <div className={styles.actions}>
          <button type="submit" disabled={saving}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AccountSettings;
